<template>
    <div class="field-settings">
        <div class="field-settings__grid">
            <!-- Width -->
            <Field
                v-model="localField.width"
                :field="{
                    name: lang('fields::fields.field-editor.width.label'),
                    slug: 'width',
                    type: 'select',
                    adminComponent: 'AdminSelect',
                    options: widthOptions,
                }"
                label-position="top"
                @change="emitUpdate"
            />

            <!-- Label Position -->
            <Field
                v-model="localField.labelPosition"
                :field="{
                    name: lang('fields::fields.field-editor.label_position.label'),
                    slug: 'labelPosition',
                    type: 'select',
                    adminComponent: 'AdminSelect',
                    options: labelPositionOptions,
                }"
                label-position="top"
                @change="emitUpdate"
            />
        </div>

        <!-- Placeholder -->
        <Field
            v-model="localField.placeholder"
            :field="{
                name: lang('fields::fields.field-editor.placeholder'),
                slug: 'placeholder',
                type: 'text',
                adminComponent: 'AdminText',
            }"
            label-position="top"
            @change="emitUpdate"
        />

        <!-- Help text -->
        <Field
            v-model="localField.help"
            :field="{
                name: lang('fields::fields.field-editor.help_text.label'),
                slug: 'help',
                type: 'textarea',
                adminComponent: 'AdminTextarea',
                help: lang('fields::fields.field-editor.help_text.description'),
            }"
            label-position="top"
            @change="emitUpdate"
        />

        <!-- Wrapper CSS class -->
        <Field
            v-model="localField.wrapperClass"
            :field="{
                name: lang('fields::fields.field-editor.wrapper_class.label'),
                slug: 'wrapperClass',
                type: 'text',
                adminComponent: 'AdminText',
                help: lang('fields::fields.field-editor.wrapper_class.description'),
            }"
            label-position="top"
            @change="emitUpdate"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { lang } from '@/lang/composable';

interface FieldData {
    slug: string;
    name: string;
    type: string;
    width?: string | null;
    labelPosition?: string;
    placeholder?: string;
    help?: string;
    wrapperClass?: string;
    [key: string]: any;
}

interface Props {
    field: FieldData;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    (event: 'update:field', field: FieldData): void;
}>();

const localField = ref<FieldData>({ ...props.field });

watch(
    () => props.field,
    (newField) => {
        localField.value = { ...newField };
    },
    { deep: true },
);

const widthOptions = computed(() => [
    { id: null, name: lang('fields::fields.field-editor.width.options.auto') },
    { id: '25%', name: lang('fields::fields.field-editor.width.options.25') },
    { id: '33%', name: lang('fields::fields.field-editor.width.options.33') },
    { id: '50%', name: lang('fields::fields.field-editor.width.options.50') },
    { id: '75%', name: lang('fields::fields.field-editor.width.options.75') },
    { id: '100%', name: lang('fields::fields.field-editor.width.options.100') },
]);

const labelPositionOptions = computed(() => [
    { id: 'top', name: lang('fields::fields.field-editor.label_position.options.top') },
    { id: 'left', name: lang('fields::fields.field-editor.label_position.options.left') },
    { id: 'none', name: lang('fields::fields.field-editor.label_position.options.none') },
]);

const emitUpdate = () => {
    emit('update:field', { ...localField.value });
};
</script>
